import React, { useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import Tooltip from './UI/Tooltip';

const AggregatedDashboard = ({ executions, searchSources = [], viewMode = 'detailed' }) => {
  const { isDark } = useTheme();
  // Data aggregation functions
  const aggregatedData = useMemo(() => {
    if (!executions || executions.length === 0) {
      return {
        averageClientScore: 0,
        clientVisibilityRate: 0,
        totalCompetitors: 0,
        topCompetitors: [],
        topTrackedCompetitors: [],
        topFoundCompetitors: [],
        engineStats: {},
        engineVisibilityScores: {},
        bestQueries: [],
        totalSuccessful: 0,
        totalFailed: 0,
        topSources: [],
        totalUniqueSources: 0,
        totalCitations: 0
      };
    }

    const successful = executions.filter(exec => exec.status === 'completed');
    const failed = executions.filter(exec => exec.status === 'failed');
    const totalExecutions = successful.length; // For visibility rate calculation

    // Calculate average client score
    const scores = successful
      .map(exec => exec.ai_analysis_results?.summary?.overallClientScore || 0)
      .filter(score => score > 0);
    const averageClientScore = scores.length > 0 ? 
      Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length) : 0;

    // Calculate client visibility rate
    const clientMentions = successful.filter(exec => 
      exec.ai_analysis_results?.summary?.clientMentions > 0
    ).length;
    const clientVisibilityRate = successful.length > 0 ? 
      Math.round((clientMentions / successful.length) * 100) : 0;

    // Aggregate competitors and separate tracked vs found
    const competitorMap = new Map();
    const trackedCompetitorMap = new Map();
    const foundCompetitorMap = new Map();
    
    successful.forEach(exec => {
      const competitors = exec.ai_analysis_results?.summary?.topCompetitors || [];
      const trackedCompetitors = exec.ai_analysis_results?.summary?.trackedCompetitors || [];
      const foundCompetitors = exec.ai_analysis_results?.summary?.foundCompetitors || [];
      
      // Process all competitors for total count
      competitors.forEach(comp => {
        if (competitorMap.has(comp.name)) {
          const existing = competitorMap.get(comp.name);
          existing.mentions += comp.mentions || 0;
          existing.queries.add(exec.searchTerm);
          existing.totalScore += comp.averageScore || 0;
          existing.appearances++;
          existing.isTracked = existing.isTracked || comp.isTracked;
        } else {
          competitorMap.set(comp.name, {
            name: comp.name,
            mentions: comp.mentions || 0,
            queries: new Set([exec.searchTerm]),
            totalScore: comp.averageScore || 0,
            appearances: 1,
            isTracked: comp.isTracked || false
          });
        }
      });
      
      // Process tracked competitors separately
      trackedCompetitors.forEach(comp => {
        if (trackedCompetitorMap.has(comp.name)) {
          const existing = trackedCompetitorMap.get(comp.name);
          existing.mentions += comp.mentions || 0;
          existing.queries.add(exec.searchTerm);
          existing.totalScore += comp.averageScore || 0;
          existing.appearances++;
        } else {
          trackedCompetitorMap.set(comp.name, {
            name: comp.name,
            mentions: comp.mentions || 0,
            queries: new Set([exec.searchTerm]),
            totalScore: comp.averageScore || 0,
            appearances: 1,
            isTracked: true
          });
        }
      });
      
      // Process found competitors separately
      foundCompetitors.forEach(comp => {
        if (foundCompetitorMap.has(comp.name)) {
          const existing = foundCompetitorMap.get(comp.name);
          existing.mentions += comp.mentions || 0;
          existing.queries.add(exec.searchTerm);
          existing.totalScore += comp.averageScore || 0;
          existing.appearances++;
        } else {
          foundCompetitorMap.set(comp.name, {
            name: comp.name,
            mentions: comp.mentions || 0,
            queries: new Set([exec.searchTerm]),
            totalScore: comp.averageScore || 0,
            appearances: 1,
            isTracked: false
          });
        }
      });
    });

    // Convert to sorted arrays
    const processCompetitorMap = (map) => Array.from(map.values())
      .map(comp => ({
        ...comp,
        queries: comp.queries.size,
        averageScore: Math.round(comp.totalScore / comp.appearances),
        // Calculate visibility rate: appearances out of total successful executions
        visibilityRate: totalExecutions > 0 ? Math.round((comp.appearances / totalExecutions) * 100) : 0
      }))
      .sort((a, b) => b.visibilityRate - a.visibilityRate || b.mentions - a.mentions || b.queries - a.queries);

    const topCompetitors = processCompetitorMap(competitorMap).slice(0, 8);
    const topTrackedCompetitors = processCompetitorMap(trackedCompetitorMap).slice(0, 5);
    const topFoundCompetitors = processCompetitorMap(foundCompetitorMap).slice(0, 5);

    // Engine statistics and visibility scores
    const engineStats = {};
    const engineVisibilityScores = {};
    
    successful.forEach(exec => {
      if (exec.results?.engines) {
        Object.entries(exec.results.engines).forEach(([engine, result]) => {
          if (!engineStats[engine]) {
            engineStats[engine] = { success: 0, total: 0 };
            engineVisibilityScores[engine] = { totalScore: 0, count: 0, clientMentions: 0 };
          }
          engineStats[engine].total++;
          if (result.status === 'success') {
            engineStats[engine].success++;
          }
        });
      }
      
      // Calculate visibility scores per engine
      if (exec.ai_analysis_results?.engines) {
        Object.entries(exec.ai_analysis_results.engines).forEach(([engine, engineAnalysis]) => {
          if (!engineVisibilityScores[engine]) {
            engineVisibilityScores[engine] = { totalScore: 0, count: 0, clientMentions: 0 };
          }
          
          // Score is in clientAnalysis.score, not directly on engineAnalysis
          const score = engineAnalysis.clientAnalysis?.score || 0;
          engineVisibilityScores[engine].totalScore += score;
          engineVisibilityScores[engine].count++;
          
          // Check for client mentions
          if (engineAnalysis.clientAnalysis?.mentions > 0) {
            engineVisibilityScores[engine].clientMentions++;
          }
        });
      }
    });
    
    // Calculate average visibility scores
    Object.keys(engineVisibilityScores).forEach(engine => {
      const stats = engineVisibilityScores[engine];
      stats.averageScore = stats.count > 0 ? Math.round(stats.totalScore / stats.count) : 0;
      stats.visibilityRate = stats.count > 0 ? Math.round((stats.clientMentions / stats.count) * 100) : 0;
    });

    // Best performing queries
    const bestQueries = successful
      .map(exec => ({
        query: exec.searchTerm,
        score: exec.ai_analysis_results?.summary?.overallClientScore || 0,
        competitors: exec.ai_analysis_results?.summary?.topCompetitors?.length || 0,
        clientMentioned: (exec.ai_analysis_results?.summary?.clientMentions || 0) > 0
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);

    // Aggregate search sources
    const sourceMap = new Map();
    const uniqueDomains = new Set();
    
    searchSources.forEach(source => {
      if (source.domain) {
        uniqueDomains.add(source.domain);
        
        if (sourceMap.has(source.domain)) {
          const existing = sourceMap.get(source.domain);
          existing.count++;
          existing.engines.add(source.engine);
          // Use the category from the first source (all sources from same domain should have same category)
          if (!existing.category && source.category) {
            existing.category = source.category;
          }
        } else {
          sourceMap.set(source.domain, {
            domain: source.domain,
            count: 1,
            engines: new Set([source.engine]),
            category: source.category || 'Unknown'
          });
        }
      }
    });
    
    // Convert to sorted array
    const topSources = Array.from(sourceMap.values())
      .map(source => ({
        ...source,
        engines: Array.from(source.engines)
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return {
      averageClientScore,
      clientVisibilityRate,
      totalCompetitors: topCompetitors.length,
      topCompetitors,
      topTrackedCompetitors,
      topFoundCompetitors,
      engineStats,
      engineVisibilityScores,
      bestQueries,
      totalSuccessful: successful.length,
      totalFailed: failed.length,
      topSources,
      totalUniqueSources: uniqueDomains.size,
      totalCitations: searchSources.length
    };
  }, [executions, searchSources]);

  const getEngineIcon = (engine) => {
    const engineName = engine.toLowerCase();
    const logoMap = {
      'openai': '/logos/chatgpt.png',
      'chatgpt': '/logos/chatgpt.png',
      'gemini': '/logos/gemini.png',
      'perplexity': '/logos/perplexity.png'
    };
    
    const logoSrc = logoMap[engineName];
    if (logoSrc) {
      return (
        <img 
          src={logoSrc} 
          alt={`${engine} logo`}
          className="w-6 h-6 rounded-full object-cover"
        />
      );
    }
    return <div className={`w-6 h-6 rounded-full ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}></div>;
  };

  // Group sources by category (using database categories)
  const categorizedSources = useMemo(() => {
    const categories = {};
    
    aggregatedData.topSources.forEach(source => {
      const category = source.category || 'Unknown';
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(source);
    });
    
    return categories;
  }, [aggregatedData.topSources]);


  return (
    <div className={`glass-card rounded-xl overflow-visible ${viewMode === 'simple' ? 'p-12' : 'p-16'}`}>
      <div className={`flex items-center gap-2 ${viewMode === 'simple' ? 'mb-8' : 'mb-12'}`}>
        <svg className={`w-5 h-5 ${isDark ? 'text-accent-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h2 className={`heading-mono text-lg font-bold text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>Aggregated Analysis</h2>
      </div>

      {/* Key Metrics Grid */}
      <div className={`grid grid-cols-2 md:grid-cols-3 overflow-visible ${viewMode === 'simple' ? 'gap-8 mb-16' : 'gap-10 mb-20'}`}>
        <div className={`glass-dark rounded-lg relative overflow-visible ${viewMode === 'simple' ? 'p-6' : 'p-8'}`}>
          <Tooltip position="bottom" content="Percentage of searches where your brand was mentioned in AI responses">
            <div className={`text-mono text-sm mb-1 flex items-center gap-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              {viewMode === 'detailed' ? 'Client Visibility' : 'Visibility'}
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </Tooltip>
          <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>{aggregatedData.clientVisibilityRate}%</div>
          {viewMode === 'detailed' && (
            <div className={`text-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>queries mentioned</div>
          )}
        </div>
        
        <div className={`glass-dark rounded-lg relative overflow-visible ${viewMode === 'simple' ? 'p-6' : 'p-8'}`}>
          <Tooltip position="bottom" content="Number of unique competitors mentioned across all AI engines">
            <div className={`text-mono text-sm mb-1 flex items-center gap-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              {viewMode === 'detailed' ? 'Competitors Found' : 'Competitors'}
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </Tooltip>
          <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>{aggregatedData.totalCompetitors}</div>
          {viewMode === 'detailed' && (
            <div className={`text-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>unique companies</div>
          )}
        </div>
        
        <div className={`glass-dark rounded-lg relative overflow-visible ${viewMode === 'simple' ? 'p-6' : 'p-8'}`}>
          <Tooltip position="bottom" content="Percentage of searches that successfully returned results from AI engines">
            <div className={`text-mono text-sm mb-1 flex items-center gap-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Success Rate
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </Tooltip>
          <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>
            {aggregatedData.totalSuccessful + aggregatedData.totalFailed > 0 ? 
              Math.round((aggregatedData.totalSuccessful / (aggregatedData.totalSuccessful + aggregatedData.totalFailed)) * 100) : 0}%
          </div>
          {viewMode === 'detailed' && (
            <div className={`text-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{aggregatedData.totalSuccessful}/{aggregatedData.totalSuccessful + aggregatedData.totalFailed} queries</div>
          )}
        </div>
      </div>

      {/* Visibility Score by Model */}
      <div className={viewMode === 'simple' ? 'mb-16' : 'mb-20'}>
        <h3 className={`text-mono text-md font-bold ${viewMode === 'simple' ? 'mb-4' : 'mb-6'} flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
          <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          {viewMode === 'detailed' ? 'Visibility Score by Model' : 'Model Scores'}
        </h3>
        <div className={`grid grid-cols-1 md:grid-cols-3 ${viewMode === 'simple' ? 'gap-6' : 'gap-8'}`}>
          {Object.entries(aggregatedData.engineVisibilityScores).map(([engine, stats]) => (
            <div key={engine} className={`glass-card rounded-lg ${viewMode === 'simple' ? 'p-6' : 'p-8'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {getEngineIcon(engine)}
                  <span className={`text-mono font-medium capitalize ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    {engine}
                  </span>
                </div>
                <div className={`text-2xl font-bold ${
                  stats.visibilityRate >= 50 ? 'text-green-400' : 
                  stats.visibilityRate >= 25 ? 'text-yellow-400' : 
                  'text-red-400'
                }`}>
                  {stats.visibilityRate}%
                </div>
              </div>
              {viewMode === 'detailed' ? (
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className={`text-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Client Mentions
                    </span>
                    <span className={`text-mono text-xs font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {stats.clientMentions}/{stats.count}
                    </span>
                  </div>
                  <div className={`w-full ${isDark ? 'bg-gray-600' : 'bg-gray-300'} rounded-full h-2 mt-2`}>
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${
                        stats.visibilityRate >= 50 ? 'bg-green-400' : 
                        stats.visibilityRate >= 25 ? 'bg-yellow-400' : 
                        'bg-red-400'
                      }`}
                      style={{ width: `${stats.visibilityRate}%` }}
                    />
                  </div>
                </div>
              ) : (
                <div className={`text-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  mentioned {stats.clientMentions}/{stats.count}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className={`grid md:grid-cols-2 ${viewMode === 'simple' ? 'gap-12' : 'gap-16'}`}>
        {/* Top Competitors */}
        <div>
          <h3 className={`text-mono text-md font-bold ${viewMode === 'simple' ? 'mb-4' : 'mb-6'} flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Competitor Analysis
          </h3>
          
          {/* Tracked Competitors */}
          {aggregatedData.topTrackedCompetitors && aggregatedData.topTrackedCompetitors.length > 0 && (
            <div className="mb-4">
              <h4 className={`text-mono text-xs font-medium uppercase mb-2 flex items-center gap-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <span className="glass-badge glass-badge-info text-mono text-xs px-2 py-0.5">Tracked</span>
                Competitors ({aggregatedData.topTrackedCompetitors.length})
              </h4>
              <div className="space-y-2">
                {aggregatedData.topTrackedCompetitors.map((competitor, idx) => (
                  <div key={competitor.name} className="flex items-center justify-between p-3 glass-dark rounded-lg border border-blue-500/20">
                    {viewMode === 'detailed' ? (
                      <>
                        <div className="flex items-center gap-3">
                          <div>
                            <div className={`text-mono font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{competitor.name}</div>
                            <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {competitor.queries} queries • {competitor.mentions} mentions
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex flex-col items-end">
                            <span className={`text-mono text-sm font-semibold ${
                              competitor.visibilityRate >= 50 ? 'text-green-400' : 
                              competitor.visibilityRate >= 25 ? 'text-yellow-400' : 
                              'text-red-400'
                            }`}>
                              {competitor.visibilityRate || 0}%
                            </span>
                            <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>visibility</div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className={`text-mono font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{competitor.name}</span>
                        </div>
                        <span className={`text-mono text-sm font-semibold ${
                          competitor.visibilityRate >= 50 ? 'text-green-400' : 
                          competitor.visibilityRate >= 25 ? 'text-yellow-400' : 
                          'text-red-400'
                        }`}>{competitor.visibilityRate || 0}%</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Found Competitors */}
          {aggregatedData.topFoundCompetitors && aggregatedData.topFoundCompetitors.length > 0 && (
            <div>
              <h4 className={`text-mono text-xs font-medium uppercase mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Other Competitors Found ({aggregatedData.topFoundCompetitors.length})
              </h4>
              <div className="space-y-2">
                {aggregatedData.topFoundCompetitors.map((competitor, idx) => (
                  <div key={competitor.name} className="flex items-center justify-between p-3 glass-dark rounded-lg">
                    {viewMode === 'detailed' ? (
                      <>
                        <div className="flex items-center gap-3">
                          <div>
                            <div className={`text-mono font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{competitor.name}</div>
                            <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {competitor.queries} queries • {competitor.mentions} mentions
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex flex-col items-end">
                            <span className={`text-mono text-sm font-semibold ${
                              competitor.visibilityRate >= 50 ? 'text-green-400' : 
                              competitor.visibilityRate >= 25 ? 'text-yellow-400' : 
                              'text-red-400'
                            }`}>
                              {competitor.visibilityRate || 0}%
                            </span>
                            <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>visibility</div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <span className={`text-mono font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{competitor.name}</span>
                        </div>
                        <span className={`text-mono text-sm font-semibold ${
                          competitor.visibilityRate >= 50 ? 'text-green-400' : 
                          competitor.visibilityRate >= 25 ? 'text-yellow-400' : 
                          'text-red-400'
                        }`}>{competitor.visibilityRate || 0}%</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* No competitors message */}
          {(!aggregatedData.topTrackedCompetitors || aggregatedData.topTrackedCompetitors.length === 0) && 
           (!aggregatedData.topFoundCompetitors || aggregatedData.topFoundCompetitors.length === 0) && (
            <div className={`text-center py-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <p className="text-mono text-sm">No competitors found in analysis</p>
            </div>
          )}
        </div>

        {/* Top Sources */}
        <div>
          <h3 className={`text-mono text-md font-bold ${viewMode === 'simple' ? 'mb-4' : 'mb-6'} flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <svg className={`w-4 h-4 ${isDark ? 'text-accent-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            Top Sources
            {viewMode === 'detailed' && (
              <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                ({aggregatedData.totalUniqueSources} unique • {aggregatedData.totalCitations} citations)
              </span>
            )}
          </h3>
          {aggregatedData.topSources.length > 0 ? (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {Object.entries(categorizedSources).map(([category, sources]) => (
                <div key={category}>
                  <h4 className={`text-mono text-xs font-medium uppercase mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {category} ({sources.length})
                  </h4>
                  <div className="space-y-2">
                    {sources.map((source, idx) => (
                      <div key={source.domain} className="flex items-center justify-between p-3 glass-dark rounded-lg">
                        {viewMode === 'detailed' ? (
                          <>
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex-1 min-w-0">
                                <div className={`text-mono font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>
                                  {source.domain}
                                </div>
                                <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {source.engines.length === 1 ? source.engines[0] : `${source.engines.length} engines`}
                                </div>
                              </div>
                            </div>
                            <div className="text-right ml-2">
                              <div className={`text-mono text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>{source.count}</div>
                              <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>citations</div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className={`text-mono font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>{source.domain}</span>
                            </div>
                            <span className={`text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>{source.count}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={`text-center py-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <p className="text-mono text-sm">No sources found</p>
              {viewMode === 'detailed' && (
                <p className={`text-mono text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  Sources will appear after search execution
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Competitor Analysis Table */}
      {viewMode === 'detailed' && aggregatedData.topTrackedCompetitors && aggregatedData.topTrackedCompetitors.length > 0 && (
        <div className="mt-12">
          <h3 className={`text-mono text-md font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Competitor Analysis
            <span className="glass-badge glass-badge-info text-mono text-xs px-2 py-0.5">Tracked</span>
          </h3>
          <div className="glass-card rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <th className={`text-left text-mono text-xs font-medium uppercase p-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Competitor
                    </th>
                    <th className={`text-center text-mono text-xs font-medium uppercase p-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Mentions
                    </th>
                    <th className={`text-center text-mono text-xs font-medium uppercase p-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Queries
                    </th>
                    <th className={`text-center text-mono text-xs font-medium uppercase p-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Visibility
                    </th>
                    <th className={`text-right text-mono text-xs font-medium uppercase p-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Trend
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {aggregatedData.topTrackedCompetitors.map((competitor, idx) => {
                    // Use the pre-calculated visibility rate from the competitor object
                    const visibilityRate = competitor.visibilityRate || 0;
                    return (
                      <tr key={competitor.name} className={`border-b ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} hover:bg-white/5 transition-colors`}>
                        <td className="p-4">
                          <div className={`text-mono font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                            {competitor.name}
                          </div>
                          <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Tracked Competitor
                          </div>
                        </td>
                        <td className="text-center p-4">
                          <span className={`text-mono font-semibold text-lg ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>
                            {competitor.mentions}
                          </span>
                        </td>
                        <td className="text-center p-4">
                          <span className={`text-mono ${isDark ? 'text-white' : 'text-gray-800'}`}>
                            {competitor.queries}
                          </span>
                        </td>
                        <td className="text-center p-4">
                          <div className="flex flex-col items-center">
                            <span className={`text-mono font-medium ${visibilityRate >= 50 ? 'text-green-400' : visibilityRate >= 25 ? 'text-yellow-400' : 'text-red-400'}`}>
                              {visibilityRate}%
                            </span>
                            <div className={`w-16 h-1.5 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-300'} mt-1`}>
                              <div 
                                className={`h-full rounded-full transition-all duration-300 ${
                                  visibilityRate >= 50 ? 'bg-green-400' : 
                                  visibilityRate >= 25 ? 'bg-yellow-400' : 
                                  'bg-red-400'
                                }`}
                                style={{ width: `${Math.min(visibilityRate, 100)}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="text-right p-4">
                          <div className="flex items-center justify-end gap-1">
                            {idx === 0 ? (
                              <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                              </svg>
                            ) : idx < 3 ? (
                              <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                              </svg>
                            )}
                            <span className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              #{idx + 1}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {aggregatedData.topFoundCompetitors && aggregatedData.topFoundCompetitors.length > 0 && (
              <div className={`p-4 border-t ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`}>
                <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                  Additionally found {aggregatedData.topFoundCompetitors.length} other competitors:
                </div>
                <div className="flex flex-wrap gap-2">
                  {aggregatedData.topFoundCompetitors.slice(0, 10).map(comp => (
                    <span key={comp.name} className="glass-badge text-mono text-xs px-2 py-1">
                      {comp.name} ({comp.mentions})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Best Performing Queries */}
      {aggregatedData.bestQueries.length > 0 && viewMode === 'detailed' && (
        <div className="mt-6">
          <h3 className={`text-mono text-md font-bold ${viewMode === 'simple' ? 'mb-4' : 'mb-6'} flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            Top Performing Queries
          </h3>
          <div className={`glass-dark rounded-lg relative overflow-visible ${viewMode === 'simple' ? 'p-6' : 'p-8'}`}>
            <div className="space-y-2">
              {aggregatedData.bestQueries.map((query, idx) => (
                <div key={idx} className={`flex items-center justify-between py-2 border-b last:border-b-0 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <div className="flex-1 min-w-0">
                    <div className={`text-mono text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>{query.query}</div>
                    <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {query.competitors} competitors • 
                      {query.clientMentioned ? ' Client mentioned' : ' Client not mentioned'}
                    </div>
                  </div>
                  <div className="ml-4">
                    <span className={`text-mono text-sm font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>{query.score}/100</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AggregatedDashboard;